import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Events swallowed while the UI is locked. They are captured on `document`, so
 * document-level listeners registered later — notably the details-sidebar
 * outside-click (`document:mousedown`) and Escape (`document:keydown`) handlers —
 * never see them, and nothing bubbles back down to any component binding either.
 */
const BLOCKED_EVENT_TYPES = [
  'pointerdown',
  'pointerup',
  'mousedown',
  'mouseup',
  'click',
  'dblclick',
  'auxclick',
  'contextmenu',
  'keydown',
  'keyup',
  'keypress',
  'wheel',
  'touchstart',
  'touchmove',
  'touchend',
  'dragstart',
  'submit',
] as const;

const BLOCKER_STYLE = [
  'position:fixed',
  'inset:0',
  'z-index:2147483000',
  'background:transparent',
  'cursor:progress',
  'touch-action:none',
].join(';');

/**
 * Freezes the whole UI while an operation must not be interrupted: a transparent
 * full-viewport blocker absorbs pointer activity and every event in
 * `BLOCKED_EVENT_TYPES` is swallowed at document capture, so no click, key press
 * or scroll reaches the application. The page keeps rendering, so whatever is on
 * screen (e.g. an open sub-sidebar) stays visible and can show its own progress.
 *
 * Lock holders are reference counted: every `lock()` needs its `unlock()`. Call
 * `reset()` from teardown paths that cannot guarantee the matching `unlock()`.
 */
@Injectable({ providedIn: 'root' })
export class InteractionLockService {
  private readonly lockedSubject = new BehaviorSubject<boolean>(false);
  private lockCount = 0;
  private blocker?: HTMLElement;

  readonly locked$: Observable<boolean> = this.lockedSubject.asObservable();

  get isLocked(): boolean {
    return this.lockCount > 0;
  }

  lock(): void {
    this.lockCount += 1;
    if (this.lockCount === 1) {
      this.engage();
    }
  }

  unlock(): void {
    if (this.lockCount === 0) {
      return;
    }
    this.lockCount -= 1;
    if (this.lockCount === 0) {
      this.release();
    }
  }

  /** Drops every outstanding lock. */
  reset(): void {
    if (this.lockCount === 0) {
      return;
    }
    this.lockCount = 0;
    this.release();
  }

  private engage(): void {
    const blocker = document.createElement('div');
    blocker.setAttribute('aria-hidden', 'true');
    blocker.style.cssText = BLOCKER_STYLE;
    document.body.appendChild(blocker);
    this.blocker = blocker;
    BLOCKED_EVENT_TYPES.forEach((type) =>
      document.addEventListener(type, this.swallowEvent, {
        capture: true,
        passive: false,
      }),
    );
    this.lockedSubject.next(true);
  }

  private release(): void {
    BLOCKED_EVENT_TYPES.forEach((type) =>
      document.removeEventListener(type, this.swallowEvent, { capture: true }),
    );
    this.blocker?.remove();
    this.blocker = undefined;
    this.lockedSubject.next(false);
  }

  private readonly swallowEvent = (event: Event): void => {
    event.stopImmediatePropagation();
    if (event.cancelable) {
      event.preventDefault();
    }
  };
}
